/** Shared numeric helpers for Stage Finance (no domain logic). */

export function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

export function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

export function clampInt(v, min, max, fallback) {
  const x = Math.round(Number(v));
  if (!Number.isFinite(x)) return fallback;
  return Math.min(max, Math.max(min, x));
}

export function nearlyEqual(a, b, eps = 0.02) {
  return Math.abs(n(a) - n(b)) <= eps;
}

export function sum(arr, key) {
  if (!arr || !arr.length) return 0;
  if (!key) return arr.reduce((s, v) => s + n(v), 0);
  return arr.reduce((s, row) => s + n(row[key]), 0);
}

/** Spread a total across weights; last bucket absorbs rounding residue so sum is exact. */
export function allocateExact(total, weights) {
  const t = n(total);
  if (!weights.length) return [];
  const w = weights.map((x) => Math.max(0, n(x)));
  const wSum = w.reduce((s, x) => s + x, 0);
  if (wSum <= 0 || t === 0) return w.map(() => 0);
  const out = w.map((x) => (t * x) / wSum);
  const allocated = out.slice(0, -1).reduce((s, x) => s + x, 0);
  out[out.length - 1] = t - allocated;
  return out;
}

/** Positive monthly deltas from a cumulative progress function over [start, end]. */
export function monthlyCurveWeights(totalMonths, startMonth, endMonth, progressFn) {
  const weights = Array(totalMonths).fill(0);
  const start = Math.max(1, startMonth);
  const end = Math.max(start, endMonth);
  const span = end - start + 1;
  let prev = 0;
  for (let m = start; m <= end && m <= totalMonths; m += 1) {
    const i = m - start + 1;
    const cum = progressFn(i, span);
    weights[m - 1] = Math.max(0, cum - prev);
    prev = cum;
  }
  // Force full consumption: renormalize so weights sum to 1.
  const s = weights.reduce((a, b) => a + b, 0);
  if (s <= 0) {
    if (end <= totalMonths) weights[end - 1] = 1;
    else weights[totalMonths - 1] = 1;
    return weights;
  }
  for (let i = 0; i < weights.length; i += 1) weights[i] /= s;
  return weights;
}
